// src/features/measuresSlice.js

import { createSlice, createAsyncThunk, createEntityAdapter, createSelector } from '@reduxjs/toolkit';
import axiosInstance from '../axiosInstance';
import { selectUnitId } from './unitsSlice'; // Import selector from unitsSlice

import { logout } from './authSlice'; // Import the global logout action

// Create an entity adapter
const measuresAdapter = createEntityAdapter();

// Initial state using the adapter's getInitialState
const initialState = measuresAdapter.getInitialState({
    status: 'idle',
    error: null,
    saveStatus: 'idle',
    saveError: null,
    createStatus: 'idle',
    createError: null,
    deleteStatus: {}, // To track delete status per measure
    deleteError: {},  // To track delete error per measure
    actionAreaStatuses: {}, // Track loading status per actionArea for fetching measures
    actionAreaErrors: {},    // Track errors per actionArea
    actionAreaCreateStatuses: {}, // Track loading status per actionArea for creating measures
    actionAreaCreateErrors: {},    // Track errors per actionArea for creating measures
});

// Selector to filter measures by action area
export const makeSelectMeasuresForActionArea = () =>
    createSelector(
        [measuresAdapter.getSelectors().selectAll, (state, actionAreaId) => actionAreaId],
        (allMeasures, actionAreaId) => allMeasures.filter((m) => m.actionarea === actionAreaId)
    );

const resolveUnitIdForActionArea = (state, actionAreaId, explicitUnitId) => {
    if (explicitUnitId) return explicitUnitId;
    const actionArea = state.actionAreas.entities[actionAreaId];
    if (actionArea?.unit) return actionArea.unit;
    return selectUnitId(state);
};

// Async thunk to fetch measures for a specific action area
export const fetchMeasures = createAsyncThunk(
    'measures/fetchMeasures',
    async ({ actionAreaId, unitId: unitIdOverride }, { getState, rejectWithValue }) => {
        const state = getState();
        const unitId = resolveUnitIdForActionArea(state, actionAreaId, unitIdOverride);
        if (!unitId) {
            return rejectWithValue({ actionAreaId, error: 'Unit ID is not available.' });
        }
        try {
            const response = await axiosInstance.get(`/api/units/${unitId}/actionareas/${actionAreaId}/measures/`);
            const measuresData = response.data;

            return { actionAreaId, measures: measuresData };
        } catch (error) {
            const errorMessage = error.response?.data?.detail || error.message || 'An unknown error occurred.';
            return rejectWithValue({ actionAreaId, error: errorMessage });
        }
    },
    {
        // Prevent duplicate requests for the same action area
        condition: ({ actionAreaId }, { getState }) => {
            const { measures } = getState();
            const actionAreaStatus = measures.actionAreaStatuses?.[actionAreaId];
            // Skip if already loading or succeeded for this action area
            if (actionAreaStatus === 'loading' || actionAreaStatus === 'succeeded') {
                return false;
            }
            return true;
        },
    }
);

// Async thunk to create a new measure
export const createMeasure = createAsyncThunk(
    'measures/createMeasure',
    async ({ actionAreaId, measure, unitId: unitIdOverride }, { getState, rejectWithValue }) => {
        const state = getState();
        const unitId = resolveUnitIdForActionArea(state, actionAreaId, unitIdOverride); // Get unitId from action area or override
        if (!unitId) {
            return rejectWithValue({ actionAreaId, error: 'Unit ID is not available.' });
        }
        try {
            if (!actionAreaId) {
                throw new Error('Action Area ID is required to create a measure.');
            }

            const newMeasure = {
                ...measure,
                actionarea: actionAreaId,
            };

            const response = await axiosInstance.post(
                `/api/units/${unitId}/actionareas/${actionAreaId}/measures/`,
                newMeasure
            );

            return { actionAreaId, measure: response.data };
        } catch (error) {
            const errorMessage =
                error.response?.data?.detail || error.message || 'An unknown error occurred.';
            return rejectWithValue({ actionAreaId, error: errorMessage });
        }
    }
);

// Async thunk to fetch a measure by ID
export const fetchMeasureById = createAsyncThunk(
    'measures/fetchMeasureById',
    async ({ actionAreaId, measureId, unitId: unitIdOverride }, { getState, rejectWithValue }) => {
        const state = getState();
        const unitId = resolveUnitIdForActionArea(state, actionAreaId, unitIdOverride); // Get unitId from action area or override
        if (!unitId) {
            return rejectWithValue({ actionAreaId, error: 'Unit ID is not available.' });
        }
        try {
            const response = await axiosInstance.get(
                `/api/units/${unitId}/actionareas/${actionAreaId}/measures/${measureId}/`
            );
            const measureData = response.data;
            return { actionAreaId, measure: measureData };
        } catch (error) {
            const errorMessage =
                error.response?.data?.detail || error.message || 'An unknown error occurred.';
            return rejectWithValue({ actionAreaId, error: errorMessage });
        }
    }
);

// Async thunk to save/update an existing measure
export const saveMeasure = createAsyncThunk(
    'measures/saveMeasure',
    async ({ actionAreaId, measure, unitId: unitIdOverride }, { getState, rejectWithValue }) => {
        const state = getState();
        const unitId = resolveUnitIdForActionArea(state, actionAreaId, unitIdOverride); // Get unitId from action area or override
        if (!unitId) {
            return rejectWithValue({ actionAreaId, error: 'Unit ID is not available.' });
        }

        try {
            const measureId = measure.id;

            if (!actionAreaId) {
                throw new Error('Action Area ID is required to save a measure.');
            }

            if (!measureId) {
                throw new Error('Measure ID is required to save a measure.');
            }

            const updatedMeasure = {
                ...measure,
                ends_at: measure.ends_at === '' ? null : measure.ends_at,
            };

            const response = await axiosInstance.put(
                `/api/units/${unitId}/actionareas/${actionAreaId}/measures/${measureId}/`,
                updatedMeasure
            );

            return { actionAreaId, measure: response.data };
        } catch (error) {
            const errorMessage = error.response?.data?.detail || error.message || 'An unknown error occurred.';
            return rejectWithValue({ actionAreaId, error: errorMessage });
        }
    }
);

export const deleteMeasure = createAsyncThunk(
    'measures/deleteMeasure',
    async ({ actionAreaId, measureId, unitId: unitIdOverride }, { getState, rejectWithValue }) => {
        const state = getState();
        const unitId = resolveUnitIdForActionArea(state, actionAreaId, unitIdOverride); // Get unitId from action area or override

        if (!unitId) {
            return rejectWithValue({ measureId, error: 'Unit ID is not available.' });
        }

        try {
            await axiosInstance.delete(`/api/units/${unitId}/actionareas/${actionAreaId}/measures/${measureId}/`);
            return { actionAreaId, measureId };
        } catch (error) {
            const errorMessage = error.response?.data?.detail || error.message || 'An unknown error occurred.';
            return rejectWithValue({ measureId, error: errorMessage });
        }
    }
);

// Slice definition
const measuresSlice = createSlice({
    name: 'measures',
    initialState,
    reducers: {
        addMeasure: measuresAdapter.addOne,
        updateMeasure: measuresAdapter.updateOne,
        removeMeasure: measuresAdapter.removeOne,
    },
    extraReducers: (builder) => {
        builder
            // Fetch Measures
            .addCase(fetchMeasures.pending, (state, action) => {
                const { actionAreaId } = action.meta.arg;
                state.actionAreaStatuses[actionAreaId] = 'loading';
                state.actionAreaErrors[actionAreaId] = null; // Reset error
            })
            .addCase(fetchMeasures.fulfilled, (state, action) => {
                const { actionAreaId, measures } = action.payload;
                state.actionAreaStatuses[actionAreaId] = 'succeeded';
                measuresAdapter.upsertMany(state, measures);
            })
            .addCase(fetchMeasures.rejected, (state, action) => {
                const { actionAreaId, error } = action.payload || {};
                state.actionAreaStatuses[actionAreaId] = 'failed';
                state.actionAreaErrors[actionAreaId] = error || action.error.message;
            })
            // Create Measure
            .addCase(createMeasure.pending, (state, action) => {
                const { actionAreaId } = action.meta.arg;
                state.actionAreaCreateStatuses[actionAreaId] = 'loading';
                state.actionAreaCreateErrors[actionAreaId] = null; // Reset error
            })
            .addCase(createMeasure.fulfilled, (state, action) => {
                const { actionAreaId, measure } = action.payload;
                state.actionAreaCreateStatuses[actionAreaId] = 'succeeded';
                measuresAdapter.addOne(state, measure);
            })
            .addCase(createMeasure.rejected, (state, action) => {
                const { actionAreaId, error } = action.payload || {};
                state.actionAreaCreateStatuses[actionAreaId] = 'failed';
                state.actionAreaCreateErrors[actionAreaId] = error || action.error.message;
            })
            // Save Measure
            .addCase(saveMeasure.pending, (state, action) => {
                const { actionAreaId } = action.meta.arg;
                state.actionAreaStatuses[actionAreaId] = 'loading';
                state.saveStatus = 'saving';
                state.saveError = null;
            })
            .addCase(saveMeasure.fulfilled, (state, action) => {
                const { actionAreaId, measure } = action.payload;
                state.actionAreaStatuses[actionAreaId] = 'succeeded';
                measuresAdapter.upsertOne(state, measure);
                state.saveStatus = 'succeeded';
            })
            .addCase(saveMeasure.rejected, (state, action) => {
                const { actionAreaId, error } = action.payload || {};
                state.actionAreaStatuses[actionAreaId] = 'failed';
                state.saveStatus = 'failed';
                state.saveError = error || action.error.message;
            })
            // Fetch Measure By ID
            .addCase(fetchMeasureById.pending, (state, action) => {
                const { actionAreaId } = action.meta.arg;
                state.actionAreaStatuses[actionAreaId] = 'loading';
                state.actionAreaErrors[actionAreaId] = null; // Reset error
            })
            .addCase(fetchMeasureById.fulfilled, (state, action) => {
                const { actionAreaId, measure } = action.payload;
                state.actionAreaStatuses[actionAreaId] = 'succeeded';
                measuresAdapter.upsertOne(state, measure);
            })
            .addCase(fetchMeasureById.rejected, (state, action) => {
                const { actionAreaId, error } = action.payload || {};
                state.actionAreaStatuses[actionAreaId] = 'failed';
                state.actionAreaErrors[actionAreaId] = error || action.error.message;
            })
            .addCase(deleteMeasure.pending, (state, action) => {
                const { measureId } = action.meta.arg;
                state.deleteStatus[measureId] = 'loading';
                state.deleteError[measureId] = null;
            })
            .addCase(deleteMeasure.fulfilled, (state, action) => {
                const { measureId } = action.payload;
                state.deleteStatus[measureId] = 'succeeded';
                measuresAdapter.removeOne(state, measureId);
            })
            .addCase(deleteMeasure.rejected, (state, action) => {
                const { measureId, error } = action.payload || {};
                state.deleteStatus[measureId] = 'failed';
                state.deleteError[measureId] = error || action.error.message;
            })
            .addCase(logout, () => initialState);
    },
});

// Export actions and selectors
export const { addMeasure, updateMeasure, removeMeasure } = measuresSlice.actions;
export const measuresSelectors = measuresAdapter.getSelectors(
    (state) => state.measures
);

// Selector to get loading status for a specific actionArea (fetching measures)
export const selectActionAreaStatus = (state, actionAreaId) =>
    state.measures.actionAreaStatuses[actionAreaId] || 'idle';

// Selector to get error for a specific actionArea (fetching measures)
export const selectActionAreaError = (state, actionAreaId) =>
    state.measures.actionAreaErrors[actionAreaId] || null;

// Selector to get create measure status for a specific actionArea
export const selectActionAreaCreateStatus = (state, actionAreaId) =>
    state.measures.actionAreaCreateStatuses[actionAreaId] || 'idle';

// Selector to get create measure error for a specific actionArea
export const selectActionAreaCreateError = (state, actionAreaId) =>
    state.measures.actionAreaCreateErrors[actionAreaId] || null;

export default measuresSlice.reducer;
